import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
// import GoogleMapReact from "google-map-react";
import {
	GoogleMap,
	MapContext,
	useJsApiLoader,
	// Marker as MarkerLibrary,
	// InfoWindow,
	OverlayView,
	OverlayViewF,
	TrafficLayer,
} from '@react-google-maps/api';
import Marker from './Marker';
import MarkerAddress from './MarkerAddress';
import MarkerShop from './MarkerShop';
import MarkerLabelled from './MarkerLabelled';
import profileController from '@/services/ProfileController';

const mapContainerStyle = {
	width: '100%',
	height: '100%',
};

const MAP_OPTIONS = {
	mapTypeControl: false,
	mapTypeId: 'roadmap',
	scrollwheel: true,
};

// Google bills every map it CREATES (Dynamic Maps), not the time one is on
// screen. The Live board used to create a new one on every return to the page
// — about ten an hour per dispatcher, three quarters of the Google bill. A map
// given a `persistKey` is created once per browser tab and kept here, outside
// React, with its div; leaving the page only detaches the div, and the next
// mount puts the same map back. A full page reload still starts over.
const persistentMaps = {};

const sameCenter = (a, b) => a?.lat === b?.lat && a?.lng === b?.lng;

const PersistentGoogleMap = ({ persistKey, center, zoom, onClick, onCreate, onAttach, children }) => {
	const [map, setMap] = useState(null);

	// The latest click handler, read when a click happens, so a new arrow
	// function on every render does not mean re-adding the listener.
	const onClickRef = useRef(onClick);
	useLayoutEffect(() => {
		onClickRef.current = onClick;
	});

	// A callback ref (with React 19's cleanup return) rather than an effect: it
	// runs when the host div is attached and detached, which is exactly when the
	// kept map has to be put in and taken out.
	const attachHost = useCallback(
		host => {
			if (!host) {
				return undefined;
			}
			let entry = persistentMaps[persistKey];
			if (!entry) {
				const div = document.createElement('div');
				div.style.width = '100%';
				div.style.height = '100%';
				entry = {
					div,
					map: new window.google.maps.Map(div, { ...MAP_OPTIONS, center, zoom }),
					center,
				};
				persistentMaps[persistKey] = entry;
				onCreate?.(entry.map);
			}
			host.appendChild(entry.div);
			const listener = entry.map.addListener('click', e => onClickRef.current?.(e));
			onAttach?.(entry.map);
			setMap(entry.map);

			return () => {
				listener.remove();
				entry.div.remove();
				setMap(null);
			};
		},
		// Once per mount: center, zoom and the callbacks only matter when the map
		// is first created, and later center changes are handled below.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[persistKey],
	);

	// Only a CHANGED center moves the map (e.g. another city after logging in
	// again without a reload). Coming back to the page keeps the view the
	// dispatcher left it in.
	useEffect(() => {
		const entry = persistentMaps[persistKey];
		if (map && center && !sameCenter(entry?.center, center)) {
			map.setCenter(center);
			entry.center = center;
		}
	}, [map, persistKey, center]);

	return (
		<>
			<div ref={attachHost} style={mapContainerStyle} />
			{/* The overlays draw into the map's own panes through portals, so they
			    only need the map from context, not a place in this DOM. */}
			{map && <MapContext.Provider value={map}>{children}</MapContext.Provider>}
		</>
	);
};

const StableOverlay = React.memo(({ marker, children }) => {
	const pos = useMemo(() => ({ lat: marker.lat, lng: marker.lng }), [marker.lat, marker.lng]);
	return (
		<OverlayViewF position={pos} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
			{children}
		</OverlayViewF>
	);
});

const MyMap = ({
	center,
	markers,
	onClick,
	defaultZoom = 8,
	big,
	shouldAnimateTo,
	traffic,
	// Show every marker's name permanently instead of one at a time on click.
	// Off everywhere in the app: the live map can carry dozens of pins, and this
	// only makes sense for a map of a handful of fixed points (the auto-assign
	// simulator). Drivers were never clickable there anyway — Marker is only
	// given `isOpen` through `big`, so clicking one did nothing at all.
	alwaysShowLabels,
	// Which screen this map is on, for counting the loads Google bills us for
	// (see profileController.countMapLoad).
	page,
	// Keep this map alive across visits to the page instead of creating (and
	// paying for) a new one each time — see PersistentGoogleMap. The Live board
	// uses it.
	persistKey,
}) => {
	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: import.meta.env.VITE_API_KEY,
		// googleMapsApiKey: 'AIzaSyACpHQprPFevW6VEBzKLyV5-8sFvgIsfds',
	});

	const [openedMarker, setOpenedMarker] = useState(null);
	// const [map, setMap] = React.useState(null);

	const mapRef = useRef();

	const onLoad = useCallback(function callback(map) {
		// This is just an example of getting and using the map instance!!! don't just blindly copy!
		// const bounds = new window.google.maps.LatLngBounds(center);
		// map.fitBounds(bounds);

		mapRef.current = map;

		// onLoad runs once per map Google creates, i.e. once per billed load.
		profileController.countMapLoad(page);

		if (shouldAnimateTo) {
			setTimeout(() => {
				mapRef.current.panTo(shouldAnimateTo); // Thessaloniki for example
			}, 400);
		}
	}, []);

	const openMarker = id => {
		// console.log("will open marker", id, openedMarker);
		setOpenedMarker(id === openedMarker ? null : id);
	};
	if (!isLoaded) {
		return null;
	}

	const handleClick = e => onClick && onClick(e.latLng.lat(), e.latLng.lng());

	const mapChildren = (
		<>
			{/* {places} */}

			{traffic && <TrafficLayer />}

			{markers.map(m => (
				<StableOverlay key={m.id} marker={m}>
					{alwaysShowLabels ? (
						<MarkerLabelled name={m?.name} type={m.type} />
					) : m.type === 'order' ? (
						<MarkerAddress
							name={m?.name}
							color={m?.color}
							isOpen={m.id === openedMarker}
							onClick={e => {
								// e.stopPropagation();
								openMarker(m.id);
							}}
						/>
					) : m.type === 'shop' ? (
						<MarkerShop
							name={m?.name}
							isOpen={m.id === openedMarker}
							onClick={e => {
								e.stopPropagation();
								openMarker(m.id);
							}}
						/>
					) : (
						<Marker
							name={m?.name}
							relativeTime={m?.relativeTime}
							onClick={e => {
								e.stopPropagation();
								openMarker(m.id);
							}}
							big={big}
						/>
					)}
				</StableOverlay>
			))}
		</>
	);

	if (persistKey) {
		return (
			<PersistentGoogleMap
				persistKey={persistKey}
				center={center}
				zoom={defaultZoom}
				onClick={handleClick}
				onCreate={onLoad}
				onAttach={map => {
					mapRef.current = map;
				}}
			>
				{mapChildren}
			</PersistentGoogleMap>
		);
	}

	return (
		<GoogleMap
			// style={{ width: "100%", height: "100%" }}
			mapContainerStyle={mapContainerStyle}
			// defaultCenter={center}
			onLoad={onLoad}
			center={center}
			zoom={defaultZoom}
			onClick={handleClick}
			options={MAP_OPTIONS}
		>
			{mapChildren}
		</GoogleMap>
	);
};

// MyMap.defaultProps = {
//   center: [37.9988844, 23.735316],
//   zoom: 9,
// };

export default MyMap;
